// src/pages/AdminIncomeTaxReport.jsx
import React, { useCallback, useMemo, useRef, useState } from "react";
import SpinnerOverlay from "../components/ui/SpinnerOverlay";
import { useLessons } from "../contexts/LessonsContext";
import MonthPicker from "../components/ui/Monthpicker";
import AdminIncomeTaxReportHeader from "../components/ui/AdminIncomeTaxReportHeader";

/* ===================== Helpers ===================== */
const pad2 = (n) => String(n).padStart(2, "0");

const currentMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};

const money = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Backend now returns rows for both teachers ('תיאום מס') and suppliers
// ('אישור ניכוי מס במקור'), each with their own withholding-tax basis.
const roleLabel = (role) => (role === "supplier" ? "ספק" : "מורה");

/* ===================== Icons ===================== */
const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

/* ===================== Small pieces ===================== */
const RoleBadge = ({ role }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${
      role === "supplier"
        ? "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200"
        : "bg-indigo-50 text-indigo-700 border-indigo-200"
    }`}
  >
    {roleLabel(role)}
  </span>
);

/* ===================== Page ===================== */
export default function AdminIncomeTaxReport() {
  const { getAdminIncomeTaxReportPreview, generateIncomeTaxPdfAndSend } = useLessons();

  const now = useRef(new Date()).current;

  const [month,      setMonth]      = useState(currentMonthStr());
  const [search,     setSearch]     = useState("");
  const [loading,    setLoading]    = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error,      setError]      = useState("");
  const [preview,    setPreview]    = useState(null);
  const [pdfUrl,     setPdfUrl]     = useState("");
  const [sent,       setSent]       = useState(false);

  const missingCount = Number(preview?.totals?.missingCount || 0);
  const teachersCount = Number(preview?.totals?.teachersCount ?? preview?.totals?.teachers ?? 0);
  const suppliersCount = Number(preview?.totals?.suppliersCount || 0);

  const totals = useMemo(() => {
    const gross           = Number(preview?.totals?.gross           || 0);
    const commission      = Number(preview?.totals?.commission      || 0);
    const afterCommission = Number(preview?.totals?.afterCommission ?? (gross - commission));
    const taxWithheld     = Number(preview?.totals?.taxWithheld     || 0);
    const netToTeachers   = afterCommission - taxWithheld;
    return { gross, commission, afterCommission, taxWithheld, netToTeachers };
  }, [preview]);

  const canGenerate = useMemo(
    () => !!preview?.ok && missingCount === 0 && !loading && !generating,
    [preview, missingCount, loading, generating]
  );

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError("");
    setPdfUrl("");
    setSent(false);
    try {
      const data = await getAdminIncomeTaxReportPreview({ month, search });
      setPreview(data);
    } catch (e) {
      setPreview(null);
      setError(e?.message || "שגיאה בטעינת דוח מס הכנסה");
    } finally {
      setLoading(false);
    }
  }, [getAdminIncomeTaxReportPreview, month, search]);

  const generatePdf = useCallback(async () => {
    setGenerating(true);
    setError("");
    setPdfUrl("");
    setSent(false);
    try {
      const res = await generateIncomeTaxPdfAndSend({ month, search });
      if (res?.pdfUrl) {
        setPdfUrl(res.pdfUrl);
        setSent(!!res.sent);
        window.open(res.pdfUrl, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      setError(e?.message || "שגיאה בהפקת PDF לדוח מס הכנסה");
    } finally {
      setGenerating(false);
    }
  }, [generateIncomeTaxPdfAndSend, month, search]);

  return (
    <>
      <AdminIncomeTaxReportHeader />

      {(loading || generating) && (
        <SpinnerOverlay variant="glow" text="אנא המתן..." dark={false} blur={true} />
      )}

      <div className="min-h-screen max-w-7xl mx-auto p-4 sm:p-6" dir="rtl">

        {/* ── Filters ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 md:p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">

            <MonthPicker
              value={month}
              onChange={setMonth}
              maxDate={now}
              minYear={2023}
              locale="he"
            />

            {/* Search */}
            <div className="flex-1 min-w-0">
              <div className="relative">
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <SearchIcon />
                </span>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); loadPreview(); }
                  }}
                  placeholder="חיפוש לפי שם, מייל או טלפון..."
                  className="w-full px-4 pr-10 py-2 rounded-lg border border-gray-200 bg-white outline-none transition-all focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={loadPreview}
                disabled={loading}
                className="flex-1 sm:flex-none sm:w-32 px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap"
              >
                {loading ? "טוען..." : "טען"}
              </button>

              <button
                onClick={generatePdf}
                disabled={!canGenerate}
                className={`flex-1 sm:flex-none sm:w-32 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap ${
                  canGenerate ? "bg-emerald-600 hover:bg-emerald-700" : "bg-emerald-300"
                }`}
              >
                {generating ? "מפיק..." : "הפק PDF"}
              </button>
            </div>
          </div>

          {/* Alerts */}
          {(error || pdfUrl || (preview?.ok && missingCount > 0)) && (
            <div className="mt-4 space-y-3">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">
                  {error}
                </div>
              )}

              {pdfUrl && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl p-3 text-sm">
                  <div className="font-semibold">
                    {sent ? "PDF הופק ונשלח בהצלחה" : "PDF הופק בהצלחה"}
                  </div>
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="underline font-medium inline-flex items-center gap-1 mt-1"
                  >
                    פתח קובץ PDF
                    <ExternalLinkIcon />
                  </a>
                </div>
              )}

              {preview?.ok && missingCount > 0 && (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-3 text-sm">
                  <span className="font-semibold">לא ניתן להפיק PDF</span> — חסרים {missingCount} נתונים.
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">👥</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-blue-700">מורים / ספקים בדוח</p>
                <p className="text-2xl font-bold text-blue-900">
                  {(teachersCount + suppliersCount).toLocaleString("he-IL")}
                </p>
                <p className="text-[11px] text-blue-600 mt-0.5">
                  {teachersCount.toLocaleString("he-IL")} מורים · {suppliersCount.toLocaleString("he-IL")} ספקים
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">₪</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-green-700">סה״כ נטו למורים ולספקים</p>
                <p className="text-2xl font-bold text-green-900">₪{money(totals.netToTeachers)}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-fuchsia-50 p-4 rounded-xl border border-purple-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">🧾</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-purple-700">ניכוי מס</p>
                <p className="text-2xl font-bold text-purple-900">₪{money(totals.taxWithheld)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Empty state ── */}
        {!preview?.ok && (
          <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-gray-500 text-2xl">🧾</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">אין נתונים להצגה</h3>
            <p className="text-gray-600 text-sm">בחר חודש ולחץ על "טען"</p>
          </div>
        )}

        {/* ── Table ── */}
        {preview?.ok && (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">

            {/* Table header bar */}
            <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <div className="text-sm font-semibold text-gray-900">פירוט דוח מס הכנסה — מורים וספקים</div>
                <div className="text-xs text-gray-500">חודש: {preview?.meta?.month || month}</div>
              </div>
            </div>

            {/*
              Column visibility strategy:
                xs–sm  (< 768px)  : שם + תפקיד | ברוטו | מס שנוכה | נטו | סטטוס
                md     (≥ 768px)  : + ת״ז
                lg     (≥ 1024px) : + עמלה + אחרי עמלה
              min-w keeps the table scrollable rather than squished
            */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] md:min-w-[640px] lg:min-w-[900px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">שם</th>
                    {/* ת״ז — hidden on xs */}
                    <th className="hidden md:table-cell px-4 py-3 text-right text-sm font-semibold text-gray-700">ת״ז</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">ברוטו</th>
                    {/* עמלה + אחרי עמלה — hidden below lg */}
                    <th className="hidden lg:table-cell px-4 py-3 text-right text-sm font-semibold text-gray-700">עמלה</th>
                    <th className="hidden lg:table-cell px-4 py-3 text-right text-sm font-semibold text-gray-700">אחרי עמלה</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">מס שנוכה</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">נטו</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">סטטוס</th>
                  </tr>
                </thead>

                <tbody>
                  {(preview?.items || []).map((t, idx) => {
                    const ok              = !!t.ok;
                    const gross           = Number(t?.grossIncome    || 0);
                    const commission      = Number(t?.commissionAmount || 0);
                    const afterCommission = Number(t?.afterCommission ?? (gross - commission));
                    const taxWithheld     = Number(t?.taxWithheld     || 0);
                    const netToTeacher    = afterCommission - taxWithheld;
                    const isSupplier = t.role === "supplier";
                    const missingAgreement = isSupplier && t.hasSupplierAgreement === false;
                    const missingTaxApproval = isSupplier && t.hasSupplierTaxApproval === false;

                    return (
                      <tr key={`${t.role || "teacher"}-${t.teacherId || idx}`} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span>{t.teacherName || "—"}</span>
                            <RoleBadge role={t.role} />
                          </div>
                        </td>

                        {/* ת״ז */}
                        <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-600">
                          {t.idNumber || "—"}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          ₪{money(gross)}
                        </td>

                        {/* עמלה */}
                        <td className="hidden lg:table-cell px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          ₪{money(commission)}
                        </td>

                        {/* אחרי עמלה */}
                        <td className="hidden lg:table-cell px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          ₪{money(afterCommission)}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          ₪{money(taxWithheld)}
                        </td>

                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                            ₪{money(netToTeacher)}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap w-fit ${
                              ok
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-red-50 text-red-700 border-red-200"
                            }`}>
                              {ok ? "תקין" : "חסר"}
                            </span>
                            {(missingAgreement || missingTaxApproval) && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border whitespace-nowrap w-fit bg-amber-50 text-amber-800 border-amber-200"
                                title={
                                  [
                                    missingAgreement ? "אין הסכם עמלה פעיל לספק — הופעלה ברירת מחדל" : null,
                                    missingTaxApproval ? "אין אישור ניכוי מס במקור מאושר — הופעלה ברירת מחדל" : null,
                                  ].filter(Boolean).join(" · ")
                                }
                              >
                                ⚠ חסר הסכם/אישור
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {!preview?.items?.length && (
                    <tr>
                      <td className="px-4 py-14 text-center text-gray-600" colSpan={8}>
                        לא נמצאו תוצאות
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Table footer — stacks on mobile */}
            {/*preview?.items?.length > 0 && (
              <div className="px-4 sm:px-6 py-3 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm">
                <div className="font-medium text-gray-700">
                  סה״כ <span className="font-bold text-gray-900">{preview.items.length}</span> מורים
                </div>
                <div className="font-semibold text-gray-900">
                  נטו לתשלום:{" "}
                  <span className="text-emerald-700 font-bold">₪{money(totals.netToTeachers)}</span>
                </div>
              </div>
            )*/}

            <div className="px-4 sm:px-6 py-2 text-[11px] text-gray-400 font-semibold border-t border-gray-100">
              * המס מחושב על הסכום לאחר עמלת הפלטפורמה. עבור ספקים — עמלה לפי הסכם ספק (supplier_agreements) ומס לפי אישור ניכוי מס במקור.
            </div>
          </div>
        )}
      </div>
    </>
  );
}